import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faDiamondExclamation } from "@fortawesome/sharp-solid-svg-icons"
import { Typography, styled } from "@mui/material"
import { useItemTooltip } from "@mui/x-charts"
import { capitalizeFirstLetter } from "../../utils/helpers/stringHelper"
import { extraColors } from "../../utils/theme"
import type { ExtendedHeatmapItem } from "./WastewaterHeatmap"
import {
  WASTEWATER_CHART_COLORS,
  type HeatmapDataPointDetails,
} from "./wastewaterHeatmapConstants"
import { hasActivity, isRareDisease } from "./WastewaterUtils"

// Heatmap-specific tooltip styled components
const HeatmapTooltipContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  minWidth: 174,
  height: "100%",
})

const HeatmapTooltipContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(8px)",
  border: `1px solid ${extraColors.dark10}`,
  borderRadius: 2,
  width: "100%",
  zIndex: 1,
})

const HeatmapActivityChip = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  padding: "1px 6px",
  border: "1px solid",
  borderRadius: 2,
  width: "100%",
  textAlign: "center",
})

const InfoSection = styled("div")({
  display: "grid",
  gap: 6,
  width: "100%",
})

const InfoRow = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  textTransform: "uppercase",
}))

const Label = styled(Typography)({
  color: extraColors.medium,
})

const Value = styled(Typography)({
  color: extraColors.white,
})

const TooltipArrow = styled("div")<{ direction?: "up" | "down" }>(
  ({ direction = "down" }) => ({
    top: -7,
    width: 35,
    height: 7,
    zIndex: 2,
    marginBottom: -1,
    display: "flex",
    justifyContent: "center",
    ...(direction === "up" && {
      transform: "rotate(180deg)",
    }),
  })
)

const ArrowSvg = () => (
  <svg
    width="12"
    height="6"
    viewBox="0 0 12 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 6L0 0H12L6 6Z"
      fill="rgba(0, 0, 0, 0.5)"
      stroke={extraColors.dark10}
      strokeWidth="1"
    />
    {/* Cover the top border to blend with tooltip */}
    <path d="M1 0H11" stroke="rgba(0, 0, 0, 0.5)" strokeWidth="2" />
  </svg>
)

interface HeatmapTooltipProps {
  heatmapData: ExtendedHeatmapItem[]
  pathogens: string[]
}

export const HeatmapTooltip = ({
  heatmapData,
  pathogens,
}: HeatmapTooltipProps) => {
  const tooltipData = useItemTooltip<"heatmap">()
  if (!tooltipData) return null

  const { identifier } = tooltipData
  const { xIndex, yIndex } = identifier
  const seriesData = heatmapData.find(
    item => item[0] === xIndex && item[1] === yIndex
  ) as readonly [number, number, number, HeatmapDataPointDetails] | undefined
  if (!seriesData) return null

  const details = seriesData[3]
  const pathogen = pathogens[seriesData[1]] ?? ""
  // const pathogenType = undefined // TODO: get from data, perhaps a display name?

  const { isAnomaly, sampleEndDate, sampleSite, copies, value: level } = details
  const color = WASTEWATER_CHART_COLORS[level]
  const isRare = isRareDisease(pathogen)

  const activityText = hasActivity(level)
    ? `Activity Level - ${capitalizeFirstLetter(level)}`
    : level

  // hide useless empty data, but keep if a sample was actually taken
  return (
    <HeatmapTooltipContainer>
      <HeatmapTooltipContent>
        <Typography variant="body2">{pathogen}</Typography>
        {/* {pathogenType && (
          <Typography variant="small1Bold">{pathogenType}</Typography>
        )} */}

        {isAnomaly && (
          <HeatmapActivityChip
            style={{
              color: color.light,
              borderColor: color.light,
              backgroundColor: color.deep,
            }}
          >
            <FontAwesomeIcon
              icon={faDiamondExclamation}
              style={{
                width: 11,
                height: 11,
                color: "inherit",
                borderColor: "inherit",
                backgroundColor: color.deep,
              }}
            />
            <Typography variant="small1Bold" color="inherit">
              Anomaly Detected
            </Typography>
          </HeatmapActivityChip>
        )}

        <HeatmapActivityChip
          style={{
            color: color.light,
            borderColor: color.muted,
            backgroundColor: color.deep,
          }}
        >
          <Typography variant="small1Bold" color="inherit">
            {activityText}
          </Typography>
        </HeatmapActivityChip>

        <InfoSection>
          <InfoRow>
            <Label variant="tiny">Sample Collected</Label>
            <Value variant="tiny">{sampleEndDate}</Value>
          </InfoRow>
          <InfoRow>
            <Label variant="tiny">Sample Site</Label>
            <Value variant="tiny">{sampleSite}</Value>
          </InfoRow>
          <InfoRow>
            <Label variant="tiny">
              {isRare ? "Copies/L Norm" : "Copies/L Norm LOD"}
            </Label>
            <Value variant="tiny">{copies}</Value>
          </InfoRow>
        </InfoSection>
      </HeatmapTooltipContent>
      <TooltipArrow direction="down">
        <ArrowSvg />
      </TooltipArrow>
    </HeatmapTooltipContainer>
  )
}
